import React, {useState, useCallback, useEffect} from "react";
import {PermissionSwitch} from "./PermissionSwitch";
import Modal from "../../../../components/common/Modal";
import {IconEdit, IconPlus, IconX} from "../../../../components/icons";
import {toast} from "react-toastify";

type PermissionState = {
  active: boolean;
  vue: boolean;
  ecriture: boolean;
};

type Rubrique = {
  id: number;
  code: string;
  libelle: string;
};

type RubriqueCategorie = {
  id: number;
  code: string;
  libelle: string;
};

type RoleData = {
  id?: number;
  libelle: string;
  code: string;
  permissions: Record<string, { vue: boolean; ecriture: boolean }>;
  used: boolean;
  partenaire?: boolean;
};

interface Props {
  isOpen: boolean;
  rubriques: Rubrique[];
  rubriqueCategories: RubriqueCategorie[];
  onClose: () => void;
  onSubmit: (data: RoleData) => Promise<void>;
  initialData?: RoleData | null;
  title?: string;
}

export const RoleModal: React.FC<Props> = (
  {
    isOpen,
    rubriques,
    rubriqueCategories,
    onClose,
    onSubmit,
    initialData,
    title = initialData ? "Modifier le rôle" : "Créer un rôle"
  }) => {
  const [roleLibelle, setRoleLibelle] = useState("");
  const [roleCode, setRoleCode] = useState("");
  const [permissions, setPermissions] = useState<Record<string, PermissionState>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [lierPartenaire, setLierPartenaire] = useState(false);

  // Init data (Create/Edit)
  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      // Mode EDIT
      setRoleLibelle(initialData.libelle || "");
      setRoleCode(initialData.code || "");
      setLierPartenaire(initialData?.partenaire || false)

      const newPermissions: Record<string, PermissionState> = {};
      // Rubriques
      rubriques.forEach(rubrique => {
        const apiPerm = initialData.permissions?.[rubrique.code];
        newPermissions[rubrique.code] = apiPerm
          ? {active: true, vue: apiPerm.vue, ecriture: apiPerm.ecriture}
          : {active: false, vue: false, ecriture: false};
      });
      // Rubrique catégories
      rubriqueCategories.forEach((rubriqueCategorie) => {
        const apiPerm = initialData.permissions?.[rubriqueCategorie.code];
        newPermissions[rubriqueCategorie.code] = apiPerm
          ? {active: true, vue: apiPerm.vue, ecriture: apiPerm.ecriture}
          : {active: false, vue: false, ecriture: false};
      });
      setPermissions(newPermissions);
    } else {
      // Mode CREATE
      setRoleLibelle("");
      setRoleCode("");
      const newPermissions: Record<string, PermissionState> = {};
      rubriques.forEach(rubrique => {
        newPermissions[rubrique.code] = {active: false, vue: false, ecriture: false};
      });
      setPermissions(newPermissions);
    }
  }, [isOpen, initialData, rubriques, rubriqueCategories]);

  // Gestion switch
  const toggleActive = useCallback((code: string) => {
    setPermissions(prev => {
      let prevData = prev;
      // Si dossier n'est pas active
      if (code === "Dossier" && !prev[code]?.active === false) {
        const {
          "Enlèvement": enlevement,
          "Entrée": entre,
          "Frais": frais,
          "Paiement & Suivi": paiementSuivi,
          ...filteredData
        } = prev;
        // Récupération des données sans "Enlèvement", "Entrée", "Frais" et "Paiement & Suivi"
        prevData = filteredData;
      }
      return {
        ...prevData,
        [code]: {
          active: !prev[code]?.active ?? false,
          vue: false,
          ecriture: false
        }
      }
    });
  }, []);

  // Toggle switch
  const toggleSwitch = useCallback((code: string, value: "vue" | "ecriture") => {
    setPermissions(prev => ({
      ...prev,
      [code]: {
        active: true,
        vue: true, // true si activé
        ecriture: value === "ecriture"
      }
    }));
  }, []);

  // Etat switch pour la modification
  const getSwitchValue = useCallback((code: string): "vue" | "ecriture" | null => {
    const p = permissions[code];
    if (!p?.active) return null;

    // Retourne ecriture si ecriture=true
    if (p.ecriture) return "ecriture";

    // Sinon vue
    return "vue";
  }, [permissions]);

  // Soumission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation : libellé + code Obligatoires
    if (!roleLibelle.trim()) {
      return;
    }
    if (!roleCode.trim()) {
      return;
    }

    setIsLoading(true);

    const payload: RoleData = {
      ...(initialData && {id: initialData.id}),
      libelle: roleLibelle.trim(),
      code: roleCode.trim(),
      permissions: Object.entries(permissions).reduce((acc, [code, v]) => {
        if (v.active) {
          acc[code] = {
            vue: true,  // Toujours true si activé
            ecriture: v.ecriture  // false(Vue) / true(Écriture)
          };
        }
        return acc;  // Non actives seront ignorées, pas insérées
      }, {} as Record<string, { vue: boolean; ecriture: boolean }>),
      used: initialData?.used,
      partenaire: lierPartenaire
    };

    // Verification des permissions avant céation / modification
    if (Object.keys(payload?.permissions).length === 0) {
      toast.error("Veuillez selectionner au moins une rubrique");
      setIsLoading(false);
    } else {
      try {
        await onSubmit(payload);
        onClose();
      } catch (error) {
        console.error('Erreur enregistrement:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <Modal openModal={true} size="lg">
      <div className="section-header flex justify-between">
        <div className="flex items-center gap-3">
          <div className="section-icon bg-green-100 text-green-600">
            {initialData ? <IconEdit/> : <IconPlus/>}
          </div>
          <span className="section-title">{title}</span>
        </div>
        <button onClick={() => !isLoading && onClose()}>
          <IconX/>
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="section-body p-3 text-sm space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="form-label">
                Nom <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={roleLibelle}
                onChange={(e) => setRoleLibelle(e.target.value)}
                className="form-input flex-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isLoading || initialData?.used}
                placeholder="Nom du rôle utilisateur"
              />
            </div>
            <div>
              <label className="form-label">
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={roleCode}
                onChange={(e) => setRoleCode(e.target.value)}
                className="form-input flex-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isLoading || initialData?.used}
                placeholder="Code du rôle utilisateur"
              />
            </div>
          </div>

          <div>
            <div className="mb-2 grid grid-cols-[1.5fr_auto_2fr] items-center text-xs font-semibold uppercase tracking-wider text-gray-500">
              <span>Rubrique</span>
            </div>

            <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-inner mb-4">
              {rubriques.map((rubrique) => {
                const state = permissions[rubrique.code] || {
                  active: false,
                  vue: false,
                  ecriture: false,
                };
                return (
                  <div
                    key={rubrique.code}
                    className="grid grid-cols-[1.5fr_auto_2fr] items-center gap-4 px-4 py-3 hover:bg-gray-50 even:bg-gray-50/50 border-b border-gray-50 last:border-b-0">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {rubrique.libelle}
                    </span>

                    <div className="justify-self-center">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <span className="text-xs font-medium text-gray-700">
                          Activé
                        </span>
                        <input
                          type="checkbox"
                          checked={state.active}
                          onChange={() => toggleActive(rubrique.code)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ml-1 shadow-sm disabled:opacity-50"
                          disabled={isLoading}
                        />
                      </label>
                    </div>

                    <div className="justify-self-center">
                      <PermissionSwitch
                        value={getSwitchValue(rubrique.code)}
                        disabled={!state.active || isLoading}
                        onChange={(v) => toggleSwitch(rubrique.code, v)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Affichage de la liste des catégories si dossier est active */}
            {(permissions?.Dossier?.active) && (
              <>
                <div className="mb-2 grid grid-cols-[1.5fr_auto_2fr] items-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <span>Catégorie</span>
                </div>

                <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-inner">
                  {rubriqueCategories.map((rubriqueCategorie) => {
                    const state = permissions[rubriqueCategorie.code] || {
                      active: false,
                      vue: false,
                      ecriture: false,
                    };
                    return (
                      <div
                        key={rubriqueCategorie.code}
                        className="grid grid-cols-[1.5fr_auto_2fr] items-center gap-4 px-4 py-3 hover:bg-gray-50 even:bg-gray-50/50 border-b border-gray-50 last:border-b-0">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {rubriqueCategorie.libelle}
                        </span>

                        <div className="justify-self-center">
                          <label className="flex items-center gap-2 cursor-pointer select-none">
                            <span className="text-xs font-medium text-gray-700">
                              Activé
                            </span>
                            <input
                              type="checkbox"
                              checked={state.active}
                              onChange={() => toggleActive(rubriqueCategorie.code)}
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ml-1 shadow-sm disabled:opacity-50"
                              disabled={isLoading}
                            />
                          </label>
                        </div>

                        <div className="justify-self-center">
                          <PermissionSwitch
                            value={getSwitchValue(rubriqueCategorie.code)}
                            disabled={!state.active || isLoading}
                            onChange={(v) => toggleSwitch(rubriqueCategorie.code, v)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="section-footer flex items-center justify-between">
          <div className="flex items-center">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={lierPartenaire}
                onChange={() => setLierPartenaire(!lierPartenaire)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ml-1 shadow-sm disabled:opacity-50"
              />
              <span className="text-xs font-medium text-gray-700">
                Lier aux partenaires
              </span>
            </label>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!roleLibelle.trim() || !roleCode.trim() || isLoading}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
            >
              {isLoading ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>

      </form>
    </Modal>
  );
};
